import React, { useState } from 'react'
import { type OracleDocumentObject, type TarotDocumentObject } from '../../graphql-types'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

export type DivinationCardObject = OracleDocumentObject | TarotDocumentObject

interface DivinationCardRendererProps {
  objects: DivinationCardObject[]
  pulledAt?: string
  classPrefix?: string
  children?: React.ReactNode
}

const splitIntoParagraphs = (text: string): string[] => text
  .split(/(?:\\n|\n)/)
  .filter((s) => s.trim().length > 0)

const formatPulledDateTime = (iso?: string) => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const datePulled = date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' })
  const timePulled = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return { datePulled, timePulled }
}

const hasInsightAndInvite = (obj: DivinationCardObject): obj is OracleDocumentObject => 'insight' in obj && 'invite' in obj

type ImageStatus = 'loading' | 'loaded' | 'error'

const CardImage: React.FC<{
  src: string
  alt: string
  classPrefix: string
}> = ({ src, alt, classPrefix }) => {
  const [status, setStatus] = useState<ImageStatus>('loading')

  return (
    <>
      {status === 'loading' && (
        <div className={`${classPrefix}__image-skeleton`} />
      )}
      {status === 'error' && (
        <div className={`${classPrefix}__image-error`}>{alt}</div>
      )}
      <img
        className={`${classPrefix}__image${status === 'loading' ? ` ${classPrefix}__image--loading` : ''}${status === 'error' ? ` ${classPrefix}__image--hidden` : ''}`}
        src={src}
        alt={alt}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
    </>
  )
}

export const DivinationCardRenderer: React.FC<DivinationCardRendererProps> = ({
  objects,
  pulledAt,
  classPrefix = 'divination-card-renderer',
  children,
}) => {
  const datePulledLabel = useTranslatedText({ keys: ['oracleCard', 'datePulled'] })
  const timePulledLabel = useTranslatedText({ keys: ['oracleCard', 'timePulled'] })
  const insightLabel = useTranslatedText({ keys: ['oracleCard', 'insight'] })
  const inviteLabel = useTranslatedText({ keys: ['oracleCard', 'invite'] })

  const dateTime = formatPulledDateTime(pulledAt)

  return (
    <div className={classPrefix}>
      {objects.map((obj) => (
        <div key={obj.objectId} className={`${classPrefix}__layout`}>
          {obj.frontImageUrl && (
            <div className={`${classPrefix}__image-container`} data-oracle-card-landing>
              <CardImage src={obj.frontImageUrl} alt={obj.displayName || ''} classPrefix={classPrefix} />
            </div>
          )}
          <div className={`${classPrefix}__content`}>
            <div className={`${classPrefix}__header`}>
              {obj.displayName && (
                <div role="heading" aria-level={3} className={`${classPrefix}__title`}>{obj.displayName}</div>
              )}
              {dateTime && (
                <div className={`${classPrefix}__meta`}>
                  <span>{datePulledLabel} {dateTime.datePulled}</span>
                  <span>{timePulledLabel} {dateTime.timePulled}</span>
                </div>
              )}
            </div>
            {hasInsightAndInvite(obj) && (
              <>
                <div className={`${classPrefix}__section`}>
                  <div role="heading" aria-level={4} className={`${classPrefix}__section-heading`}>{insightLabel}</div>
                  <div className={`${classPrefix}__section-body`}>
                    {splitIntoParagraphs(obj.insight).map((para, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </div>
                <div className={`${classPrefix}__section`}>
                  <div role="heading" aria-level={4} className={`${classPrefix}__section-heading`}>{inviteLabel}</div>
                  <div className={`${classPrefix}__section-body`}>
                    {splitIntoParagraphs(obj.invite).map((para, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </div>
              </>
            )}
            {children}
          </div>
        </div>
      ))}
    </div>
  )
}
